import { useEffect, useMemo } from 'react';
import Dispatcher from '@dealroadshow/json-rpc-dispatcher';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { useMessageManager } from '@framework/message';
import Logger from '@framework/sentry/Logger';

const computeSetSignature = (values: readonly string[]): string => [...values].sort().join(',');

interface IUseMySelectionAutoSyncOptions<T extends { id: string }, K extends string | number = string> {
  groupKey: string,
  selectedItems: T[],
  filterItem?: (item: T) => boolean,
  activeColumnsIds: readonly string[],
  isGroupStale: (groupKey: string, currentIds: readonly string[], columnsSignature: string) => boolean,
  markGroupSynced: (groupKey: string, syncedIds: readonly string[], columnsSignature: string) => void,
  replaceItems: (updates: ReadonlyMap<K, T>) => void,
  fetchFresh: (ids: string[], columnIds: readonly string[]) => Promise<T[]>,
  getId: (item: T) => K,
}

const useMySelectionAutoSync = <T extends { id: string }, K extends string | number = string>({
  groupKey,
  selectedItems,
  filterItem,
  activeColumnsIds,
  isGroupStale,
  markGroupSynced,
  replaceItems,
  fetchFresh,
  getId,
}: IUseMySelectionAutoSyncOptions<T, K>): void => {
  const { getErrorMessage } = useMessageManager();
  const groupItems = filterItem ? selectedItems.filter(filterItem) : selectedItems;
  const idsSignature = useMemo(
    () => computeSetSignature(groupItems.map((item) => item.id)),
    [groupItems],
  );
  const columnsSignature = useMemo(() => computeSetSignature(activeColumnsIds), [activeColumnsIds]);

  useEffect(() => {
    const groupIds = groupItems.map((item) => item.id);

    if (groupIds.length === 0 || !isGroupStale(groupKey, groupIds, columnsSignature)) return;

    const sync = async (): Promise<void> => {
      try {
        const fresh = await fetchFresh(groupIds, activeColumnsIds);
        replaceItems(new Map(fresh.map((item) => [getId(item), item])));
        markGroupSynced(groupKey, groupIds, columnsSignature);
      } catch (error: unknown) {
        if (Dispatcher.isAbortError(error)) return;
        Logger.error({ message: 'Failed to sync My Selection group with active columns', error, context: { groupKey } });
        AlertManager.error(getErrorMessage(error));
      }
    };

    sync();
  }, [
    groupKey,
    idsSignature,
    columnsSignature,
    isGroupStale,
    markGroupSynced,
    replaceItems,
    activeColumnsIds,
    fetchFresh,
    getId,
  ]);
};

export default useMySelectionAutoSync;
